/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import "../../../components/Drawers/DeviceDrawer.css";
import { useSelector } from "react-redux";
import { IFullDeviceDetails, ILteCell, IRadioNodesDataGridRow } from "../../../store/api/deviceConfig/types";
import { setOpenRnDrawer, setCellSaved } from "../../../store/api/devices/reducers";
import { useStoreDispatch } from "../../../store/hooks";
import { isOpenRnDrawerSelector, radioNodeSelector, selectedDeviceSelector } from "../../../store/api/devices/selectors";
import { useRadioNodeDrawer } from "./hooks";
import AddEditCellInformation from "./AddEditCellInformation";
import StatusIcon from "../../../components/Shared/Status/StatusIcon";
import { IBandData, IRnCellInfo } from "../../../store/api/devices/types";
import { DIALOGOPENFOR, PROVISION_CHECK_VAL } from "../../../globals/globals";
import { basicDialogSelector, confirmDialogSelector } from "../../../store/helpers/helperSelectors";
import { setConfirmDialog } from "../../../store/helpers/helperReducers";
import AddEditRadioInfo from "./AddEditRadioInfo";
import ConfirmDialog from "../../../components/Shared/Dialogs/ConfirmDialog";
import { constStr } from "../../../globals/constants";
import { resetCellInfo } from "../helpers";
import { Button } from "../../../components/ui/button";
import { Checkbox } from "../../../components/ui/checkbox";
import { Label } from "../../../components/ui/label";
import { Loader2 } from "lucide-react";
import { InputField } from "../../../components/Shared/Radix/Inputs/InputField";
import { Tabs, TabsList, TabsTrigger } from "../../../components/ui/tabs";
import { Sheet } from "../../../components/ui/sheet";

interface IDeviceDrawerProps {
  data?: IRadioNodesDataGridRow | undefined;
  closedButtonClicked: (arg: boolean) => void;
  rowsData?: Array<IRadioNodesDataGridRow>;
  cellList?: ILteCell | null;
  isCellDrawer?: string;
  boundRemote?: { 
    id: number; 
    label?: string; 
    floorPlanId: number; 
    floorName?: string;
    hardware?: { 
      manufacturer?: string; 
      model?: string; 
    };
    antenna?: {
      transmitterType?: string;
      azimuth?: number;
      mechanicalTilt?: number;
      mountOrientation?: number;
      height?: number;
      heightUnit?: string;
    };
  } | null;
  onUnbindRemote?: () => void;
  boundFloorData?: any | null;
  cellKpiData?: any[];
  onOpenPerformance?: (cellName: string) => void;
}

const RnDrawer: React.FC<IDeviceDrawerProps> = ({ data, closedButtonClicked, rowsData = [], cellList, isCellDrawer = "", boundRemote = null, onUnbindRemote, boundFloorData = null, cellKpiData, onOpenPerformance }) => {
  const dispatch = useStoreDispatch();
  const {
    permissions,
    zones,
    setZones,
    zoneOption,
    zones2,
    setZones2,
    zoneOption2,
    selectedRadioNode,
    cellFormDataOne,
    setCellFormDataOne,
    cellFormDataTwo,
    setCellFormDataTwo,
    isCreateCellRadioInfoOne,
    setIsCreateCellRadioInfoOne,
    isCreateCellRadioInfoTwo,
    setIsCreateCellRadioInfoTwo,
    selectedBand1,
    setSelectedBand1,
    selectedBand2,
    setSelectedBand2,
    radioNodeFormData,
    setRadioNodeFormData,
    handleSubmitRadioNode,
    selectedRadio,
    setSelectedRadio,
    isAddEditRadioOne,
    setIsAddEditRadioOne,
    isAddEditRadioTwo,
    setIsAddEditRadioTwo,
    handleDeleteRadioCell,
    onBlurHandler,
    errMsg,
    errMsg2,
    isLoadingConfigStatus,
    onBlurProvision,
    provisionIndex,
    setProvisionIndex,
    setRadioNodeList,
    isLoadingCellInfo,
    selectedBandVal,
    setSelectedBandVal,
    updateRnCellInfo,
    radioTypeCheck,
    setRadioTypeCheck,
    initialZones2,
    initialZones,
    provisionValid,
    handleSubmitProvisionData,
    handleRemoveAllCellsToZones,
    deviceDetail,
  } = useRadioNodeDrawer(data?.model || "", isCellDrawer || "");

  const isOpenRnDrawer = useSelector(isOpenRnDrawerSelector);
  const basicDialogOpenFor = useSelector(basicDialogSelector);
  const selectedDevice: IFullDeviceDetails = useSelector(selectedDeviceSelector);
  const radioNode: IRadioNodesDataGridRow = useSelector(radioNodeSelector);
  const openConfirmDialog = useSelector(confirmDialogSelector);

  React.useEffect(() => {
    if (data) {
      setRadioNodeFormData(data);
    }
  }, [data, setRadioNodeFormData]);

  React.useEffect(() => {
    if (rowsData?.length > 0) {
      setRadioNodeList(rowsData);
    }
  }, [rowsData, setRadioNodeList]);

  const handleChange = (name: string, value: string) => {
    if (name === "provisionVal") {
      setProvisionIndex(Number(value));
    } else setRadioNodeFormData({ ...radioNodeFormData, [name]: value });
  };

  const handleTabChange = (newTab: string) => {
    setSelectedRadio(newTab);
  };

  const handleUpdateCellInfoOption = (newState: boolean, type: string) => {
    if (type === "radio1") setIsCreateCellRadioInfoOne(!newState);
    else setIsCreateCellRadioInfoTwo(!newState);
  };

  const handleSetSelectedZones = (newValue: any | null, type: string) => {
    if (selectedRadio === "radio1") {
      setZones(newValue);
    } else {
      setZones2(newValue);
    }
  };

  const handleSelectBand = (val: string, type: string) => {
    if (type === "radio1") setSelectedBand1(val);
    else setSelectedBand2(val);
  };

  const handleUpdatedCellInfo = (formData: IRnCellInfo, type: string) => {
    if (type === "radio1") setCellFormDataOne(formData);
    else setCellFormDataTwo(formData);
  };

  const submitRadioNodeData = (type: string) => {
    if (type === "delete") {
      handleDeleteRadioCell();
    } else if (type === "provision") {
      handleSubmitProvisionData();
    } else handleSubmitRadioNode(type);
  };

  const updateSelectedBandVal = (val: string) => {
    setSelectedBandVal(val);
  };

  const handleAddRadioCellInfoOne = (cellId: any) => {
    setIsCreateCellRadioInfoOne(!isCreateCellRadioInfoOne);
    if (cellId) setIsAddEditRadioOne("edit");
    else setIsAddEditRadioOne("add");
  };

  const handleAddRadioCellInfoTwo = (cellId: any) => {
    setIsCreateCellRadioInfoTwo(!isCreateCellRadioInfoTwo);
    if (cellId) setIsAddEditRadioTwo("edit");
    else setIsAddEditRadioTwo("add");
  };

  const handleRnCellMsg = (cellId: any, isButton: boolean) => {
    let msg = "";
    const radioIndex = selectedRadio === "radio1" ? 0 : selectedRadio === "radio2" ? 1 : -1;
    const radiosCount = selectedRadioNode?.rnCellData?.radios;
    if (radiosCount > 0 && radioIndex >= 0) {
      const radioData = radioNodeFormData?.radios[radioIndex];
      if (radioData?.index === undefined) {
        msg = isButton ? "Add Radio" : `No radio has been added. Click "ADD RADIO" to add.`;
      } else if (radioData?.band?.toLowerCase() === "not-available") {
        msg = isButton ? "Update Radio" : `Band is not available. Please update radio from supported bandwidths. Click "UPDATE RADIO".`;
      } else {
        msg = isButton ? "Add Cell" : `No cells have been added. Click "ADD CELL" to add.`;
      }
    } else {
      msg = isButton ? "Add Cell" : `No cells have been added. Click "ADD CELL" to add.`;
    }
    return msg;
  };

  const handleAddUpdateRnCellInfo = (cellId: any) => {
    const radiosCount = selectedRadioNode?.rnCellData?.radios;
    const handleRadioAction = (radioIndex: number, handleAddRadioCellInfo: (cellId: any) => void) => {
      if (radioNodeFormData?.radios[radioIndex]?.index === undefined) {
        setRadioTypeCheck("Add");
        selectedBandVal && updateRnCellInfo();
      } else if (radioNodeFormData?.radios[radioIndex]?.band?.toLowerCase() === "not-available") {
        setRadioTypeCheck("Edit");
        selectedBandVal && updateRnCellInfo();
      } else if (radioNodeFormData?.radios[radioIndex]?.index && !cellId) {
        handleAddRadioCellInfo(cellId);
      }
    };

    if (radiosCount > 0) {
      if (selectedRadio === "radio1") {
        handleRadioAction(0, handleAddRadioCellInfoOne);
      } else if (selectedRadio === "radio2") {
        handleRadioAction(1, handleAddRadioCellInfoTwo);
      }
    }
  };

  const handleRemovesZones = () => {
    handleRemoveAllCellsToZones();
  };

  const handleSelect = (event: any) => {
    if (selectedRadio === "radio1") {
      setSelectedBand1(event.target.value);
      setSelectedBandVal(event.target.value);
    } else {
      setSelectedBand2(event.target.value);
      setSelectedBandVal(event.target.value);
    }
  };

  const isBnadEnabled = (selectedBand: string, band: string) => {
    if (selectedBand === "not-available") {
      return false;
    }
    if (selectedBand !== band) {
      return true;
    }
    if (selectedBand === band) {
      return false;
    } else return true;
  };

  const newFlatArray = (arr: string[]) => {
    return arr.flatMap((entry) => (entry?.includes("/") ? entry.split("/") : entry));
  };

  const showRadioBandData1 = () => {
    const AllBandData = selectedRadioNode?.bandData || [];
    const bandsArray =
      selectedRadio === "radio1" ? selectedRadioNode?.rnCellData?.radioBandOne || [] : selectedRadioNode?.rnCellData?.radioBandTwo || [];
    const selectedBand = selectedRadio === "radio1" ? radioNode?.radios[0]?.band || "" : radioNode?.radios[1]?.band || "";
    const currentBandValue = selectedRadio === "radio1" ? selectedBand1 : selectedBand2;
    const totalNoOfBands = newFlatArray(bandsArray);
    let radio1Bands: IBandData[] = [];
    radio1Bands = AllBandData?.filter((band) => band && totalNoOfBands.includes(band.band));
    if (radio1Bands?.length > 0) {
      return radio1Bands?.map((bandVal: IBandData, index: number) => {
        const isDisabled = isLoadingConfigStatus || isBnadEnabled(selectedBand, bandVal?.bandValue);
        return (
          <label key={index} className={`flex items-center gap-1.5 text-sm ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
            <input
              type="radio"
              name="bandSelection"
              value={bandVal?.bandValue}
              checked={currentBandValue === bandVal?.bandValue}
              disabled={isDisabled}
              onChange={handleSelect}
              style={{ accentColor: "var(--primary)" }}
            />
            {bandVal?.band}
          </label>
        );
      });
    }
  };

  const showRadioBandData2 = () => {
    const AllBandData = selectedRadioNode?.bandData || [];
    const bandsArray =
      selectedRadio === "radio1" ? selectedRadioNode?.rnCellData?.radioBandOne || [] : selectedRadioNode?.rnCellData?.radioBandTwo || [];
    const selectedBand = selectedRadio === "radio1" ? radioNode?.radios[0]?.band || "" : radioNode?.radios[1]?.band || "";
    const currentBandValue = selectedRadio === "radio1" ? selectedBand1 : selectedBand2;
    const totalNoOfBands = newFlatArray(bandsArray);
    let newArrayBand: IBandData[] = [];
    if (bandsArray.includes("B46 (LAA)")) {
      return (
        <label className="flex items-center gap-1.5 text-sm opacity-50 cursor-not-allowed">
          <input type="radio" name="bandSelection" value="B46" disabled style={{ accentColor: "var(--primary)" }} />
          B46 (LAA)
        </label>
      );
    }
    newArrayBand = AllBandData?.filter((band) => band && totalNoOfBands.includes(band.band));
    if (newArrayBand?.length > 0) {
      return newArrayBand?.map((bandVal: IBandData, index: number) => {
        const isDisabled = isLoadingConfigStatus || isBnadEnabled(selectedBand, bandVal?.bandValue);
        return (
          <label key={index} className={`flex items-center gap-1.5 text-sm ${isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
            <input
              type="radio"
              name="bandSelection"
              value={bandVal?.bandValue}
              checked={currentBandValue === bandVal?.bandValue}
              disabled={isDisabled}
              onChange={handleSelect}
              style={{ accentColor: "var(--primary)" }}
            />
            {bandVal?.band}
          </label>
        );
      });
    } else return;
  };

  const handleClosePopup = () => {
    dispatch(setConfirmDialog(undefined));
  };

  const handleCancelRadioCell = (type: string) => {
    if (type === "radio1") {
      setIsCreateCellRadioInfoOne(false);
      setIsAddEditRadioOne("add");
    } else if (type === "radio2") {
      setIsCreateCellRadioInfoTwo(false);
      setIsAddEditRadioTwo("add");
    }
  };

  const handleClose = () => {
    dispatch(setOpenRnDrawer(false));
    closedButtonClicked(false);
  };

  const isCellEditing = isCreateCellRadioInfoOne || isCreateCellRadioInfoTwo;

  const handleCellCancel = () => {
    const isProvisioned = radioNodeFormData?.id >= PROVISION_CHECK_VAL;
    if (!isProvisioned) {
      dispatch(setOpenRnDrawer(false));
      dispatch(setCellSaved(false));
    } else {
      handleUpdatedCellInfo(resetCellInfo, selectedRadio);
      handleCancelRadioCell(selectedRadio);
    }
  };

  const footer = (
    <>
      <Button variant="outline" onClick={isCellEditing ? handleCellCancel : handleClose}>
        Cancel
      </Button>
      {permissions?.hasOpCodeGroup("manage_topology") && radioNodeFormData && radioNodeFormData?.id >= PROVISION_CHECK_VAL && (
        <Button
          disabled={isLoadingConfigStatus || deviceDetail?.configurationStatus === "OFFLINE"}
          onClick={handleSubmitProvisionData}
        >
          {isLoadingConfigStatus && <Loader2 size={16} className="animate-spin mr-2" />}
          Provision
        </Button>
      )}
      {permissions?.hasOpCodeGroup("manage_topology") && (
        <Button
          disabled={radioNodeFormData?.id >= PROVISION_CHECK_VAL || isLoadingConfigStatus || selectedDevice?.status === 19 || deviceDetail?.configurationStatus === "OFFLINE"}
          onClick={() => handleSubmitRadioNode("edit")}
        >
          {isLoadingConfigStatus && <Loader2 size={16} className="animate-spin mr-2" />}
          Save
        </Button>
      )}
    </>
  );

  return (
    <>
      {basicDialogOpenFor === DIALOGOPENFOR.RNCONFIGS && (
        <AddEditRadioInfo
          onSubmitData={updateRnCellInfo}
          selectedBandVal={selectedBandVal}
          setSelectedBandVal={updateSelectedBandVal}
          configStatus={isLoadingConfigStatus}
          modelKey={data?.model || ""}
          selectedRadioNode={selectedRadioNode}
          cellType={selectedRadio}
          radioType={radioTypeCheck}
          radioIndex={selectedRadio === "radio1" ? "1" : selectedRadio === "radio2" ? "2" : ""}
        />
      )}
      {openConfirmDialog === DIALOGOPENFOR.PROVISIONRN && (
        <ConfirmDialog actionName={constStr.ok} handleClickAction={handleClosePopup}>
          {provisionValid?.provMsg}
        </ConfirmDialog>
      )}
      <Sheet open={isOpenRnDrawer} onClose={handleClose} title={data?.name || "Unknown"} footer={footer} width="560px">
        <div className="space-y-5">
          {boundRemote && (
            <div className="text-xs text-warning space-y-1">
              <div>Bound to: {boundRemote.label || `Remote ${boundRemote.id}`} on {boundRemote.floorName || "Floor"}</div>
              {(boundRemote.hardware?.model || boundRemote.antenna) && (
                <div className="text-muted-foreground space-y-1">
                  {boundRemote.hardware?.manufacturer && boundRemote.hardware?.model && (
                    <div>{boundRemote.hardware.manufacturer} - {boundRemote.hardware.model}</div>
                  )}
                  {boundRemote.antenna && (
                    <div>
                      {[
                        boundRemote.antenna.transmitterType && `Type: ${boundRemote.antenna.transmitterType}`,
                        boundRemote.antenna.azimuth !== null && boundRemote.antenna.azimuth !== undefined && `Az: ${boundRemote.antenna.azimuth}°`,
                        boundRemote.antenna.mechanicalTilt !== null && boundRemote.antenna.mechanicalTilt !== undefined && `Tilt: ${boundRemote.antenna.mechanicalTilt}°`,
                        boundRemote.antenna.height !== null && boundRemote.antenna.height !== undefined && `Height: ${boundRemote.antenna.height}${boundRemote.antenna.heightUnit || "m"}`,
                      ]
                        .filter(Boolean)
                        .join(" • ")}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <StatusIcon status={data?.status || ""} />
          {radioNodeFormData?.id < PROVISION_CHECK_VAL && (
            <div className="flex items-center gap-2">
              <Checkbox id="rn-enable" disabled checked={radioNodeFormData?.enable || false} />
              <Label htmlFor="rn-enable">Enable</Label>
            </div>
          )}

          <InputField
            name="name"
            label="Name"
            disabled={isLoadingConfigStatus || deviceDetail?.configurationStatus === "OFFLINE"}
            value={radioNodeFormData?.name || ""}
            onChange={handleChange}
          />

          <InputField
            name="model"
            label="Type/Model"
            disabled
            value={radioNodeFormData?.model || ""}
            onChange={handleChange}
          />

          <InputField
            name="macAddress"
            label="MAC Address"
            disabled
            value={radioNodeFormData?.ethernetId || ""}
            onChange={handleChange}
          />

          <InputField
            name="description"
            label="Description"
            disabled={isLoadingConfigStatus || deviceDetail?.configurationStatus === "OFFLINE"}
            value={radioNodeFormData?.description || ""}
            onChange={handleChange}
          />

          {radioNodeFormData?.id < PROVISION_CHECK_VAL && (
            <div>
              <InputField
                name="latitude"
                label="Latitude"
                type="number"
                disabled={radioNodeFormData?.id >= PROVISION_CHECK_VAL || isLoadingConfigStatus || deviceDetail?.configurationStatus === "OFFLINE"}
                value={radioNodeFormData?.latitude || ""}
                onChange={handleChange}
                onBlur={onBlurHandler}
                min={-90000000}
                max={90000000}
              />
              {errMsg?.latitude && <p className="text-xs text-destructive mt-1">{errMsg.latitude}</p>}
            </div>
          )}

          {radioNodeFormData?.id < PROVISION_CHECK_VAL && (
            <div>
              <InputField
                name="longitude"
                label="Longitude"
                type="number"
                disabled={radioNodeFormData?.id >= PROVISION_CHECK_VAL || isLoadingConfigStatus || deviceDetail?.configurationStatus === "OFFLINE"}
                value={radioNodeFormData?.longitude || ""}
                onChange={handleChange}
                onBlur={onBlurHandler}
                min={-180000000}
                max={180000000}
              />
              {errMsg?.longitude && <p className="text-xs text-destructive mt-1">{errMsg.longitude}</p>}
            </div>
          )}

          {radioNodeFormData?.id >= PROVISION_CHECK_VAL && (
            <div>
              <InputField
                name="provisionVal"
                label="Index"
                type="number"
                required
                disabled={radioNodeFormData?.id < PROVISION_CHECK_VAL || isLoadingConfigStatus}
                value={provisionIndex}
                onChange={handleChange}
                onBlur={onBlurProvision}
                min={1}
              />
              {errMsg?.provisionVal && <p className="text-xs text-destructive mt-1">{errMsg.provisionVal}</p>}
            </div>
          )}

          {/* Radio Tabs */}
          {!isLoadingCellInfo && radioNodeFormData?.radios?.length > 0 && (
            <Tabs value={selectedRadio} onValueChange={handleTabChange}>
              <TabsList className="inline-flex h-auto gap-0 rounded-none border-0 bg-transparent p-0 text-muted-foreground shrink-0">
                <TabsTrigger
                  value="radio1"
                  disabled={isLoadingConfigStatus}
                  className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                >
                  Radio 1
                </TabsTrigger>
                {Array.isArray(data?.radios) && selectedRadioNode?.rnCellData?.radios > 1 && (
                  <TabsTrigger
                    value="radio2"
                    disabled={isLoadingConfigStatus}
                    className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                  >
                    Radio 2
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>
          )}

          {/* Band Selection */}
          {!isLoadingCellInfo && (
            <div className="space-y-2 pl-2">
              <Label>Band</Label>
              <div className="flex flex-wrap items-center gap-4">
                {data?.model?.toLowerCase() === "scrn-310-04l2" ? showRadioBandData1() : showRadioBandData2()}
              </div>
            </div>
          )}

          <div className="h-px bg-border" />

          {/* Loading Cell Info */}
          {isLoadingCellInfo && (
            <div className="flex items-center gap-2">
              <Loader2 size={20} className="animate-spin text-primary" />
              <span className="text-base font-semibold text-foreground">Getting Cell Info...</span>
            </div>
          )}

          {/* Cell Information */}
          {!isLoadingCellInfo ? (
            selectedRadio === "radio1" ? (
              isCreateCellRadioInfoOne ? (
                <AddEditCellInformation
                  selectedRadioNode={selectedRadioNode}
                  cellFormData={cellFormDataOne}
                  setCellFormData={handleUpdatedCellInfo}
                  isCreateRadioCell={isCreateCellRadioInfoOne}
                  cellType={selectedRadio}
                  handleUpdateCellInfoOption={handleUpdateCellInfoOption}
                  zones={zones}
                  zoneOption={zoneOption}
                  selectedBand={selectedBand1}
                  handleSelectBand={handleSelectBand}
                  handleSetSelectedZones={handleSetSelectedZones}
                  isAddEditType={isAddEditRadioOne}
                  handleSubmit={submitRadioNodeData}
                  onBlurHandler={onBlurHandler}
                  errMsg={errMsg}
                  configStatus={isLoadingConfigStatus}
                  modelKey={data?.model || ""}
                  handleRemovesZones={handleRemovesZones}
                  initialZones={initialZones}
                  handleCancel={handleCancelRadioCell}
                  isProvisioned={radioNodeFormData?.id >= PROVISION_CHECK_VAL}
                />
              ) : (
                permissions?.hasOpCodeGroup("manage_topology") &&
                !isCreateCellRadioInfoOne && (
                  <>
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold text-foreground">Cell Information</h3>
                      <Button
                        onClick={() =>
                          radioNodeFormData?.id >= PROVISION_CHECK_VAL
                            ? handleAddRadioCellInfoOne(cellFormDataOne?.cellId)
                            : handleAddUpdateRnCellInfo(cellFormDataOne?.cellId)
                        }
                        disabled={
                          deviceDetail?.configurationStatus === "OFFLINE" ||
                          (radioNodeFormData?.id >= PROVISION_CHECK_VAL && selectedBand1?.toLowerCase() === "not-available")
                        }
                      >
                        {radioNodeFormData?.id >= PROVISION_CHECK_VAL ? "ADD CELL" : handleRnCellMsg(cellFormDataOne?.cellId, true)}
                      </Button>
                    </div>
                    <p className="text-sm" style={{ color: "var(--warning)" }}>
                      {radioNodeFormData?.id >= PROVISION_CHECK_VAL
                        ? `No cells have been added. Click "ADD CELL" to add.`
                        : handleRnCellMsg(cellFormDataOne?.cellId, false)}
                    </p>
                  </>
                )
              )
            ) : isCreateCellRadioInfoTwo ? (
              <AddEditCellInformation
                selectedRadioNode={selectedRadioNode}
                cellFormData={cellFormDataTwo}
                setCellFormData={handleUpdatedCellInfo}
                isCreateRadioCell={isCreateCellRadioInfoTwo}
                cellType={selectedRadio}
                handleUpdateCellInfoOption={handleUpdateCellInfoOption}
                zones={zones2}
                zoneOption={zoneOption2}
                selectedBand={selectedBand2}
                handleSelectBand={handleSelectBand}
                handleSetSelectedZones={handleSetSelectedZones}
                isAddEditType={isAddEditRadioTwo}
                handleSubmit={submitRadioNodeData}
                onBlurHandler={onBlurHandler}
                errMsg={errMsg2}
                configStatus={isLoadingConfigStatus}
                modelKey={data?.model || ""}
                handleRemovesZones={handleRemovesZones}
                initialZones={initialZones2}
                handleCancel={handleCancelRadioCell}
                isProvisioned={radioNodeFormData?.id >= PROVISION_CHECK_VAL}
              />
            ) : (
              permissions?.hasOpCodeGroup("manage_topology") &&
              !isCreateCellRadioInfoTwo && (
                <>
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-foreground">Cell Information</h3>
                    <Button
                      onClick={() =>
                        radioNodeFormData?.id >= PROVISION_CHECK_VAL
                          ? handleAddRadioCellInfoTwo(cellFormDataTwo?.cellId)
                          : handleAddUpdateRnCellInfo(cellFormDataTwo?.cellId)
                      }
                      disabled={
                        isLoadingConfigStatus ||
                        (radioNodeFormData?.id < PROVISION_CHECK_VAL ? false : selectedBand2?.toLowerCase() === "not-available") ||
                        deviceDetail?.configurationStatus === "OFFLINE"
                      }
                      title={radioNodeFormData?.id >= PROVISION_CHECK_VAL ? "Provision radioNode then addCell" : undefined}
                    >
                      {radioNodeFormData?.id >= PROVISION_CHECK_VAL ? "ADD CELL" : handleRnCellMsg(cellFormDataTwo?.cellId, true)}
                    </Button>
                  </div>
                  <p className="text-sm" style={{ color: "var(--warning)" }}>
                    {radioNodeFormData?.id >= PROVISION_CHECK_VAL
                      ? `No cells have been added. Click "ADD CELL" to add.`
                      : handleRnCellMsg(cellFormDataTwo?.cellId, false)}
                  </p>
                </>
              )
            )
          ) : null}

        </div>
      </Sheet>
    </>
  );
};

export default RnDrawer;
